import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import AppErrorBoundary from './components/AppErrorBoundary';
import { lazyWithReload } from './utils/chunkReload';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from './components/ui/sonner';
import { AuthProvider } from './contexts/AuthContext';
import { SystemConfigProvider } from './contexts/SystemConfigContext';
import { useAuth } from '@/contexts/AuthContext'
// ─── Eager imports — kept in the main bundle because they are on the critical
// path for first paint or are tiny wrapper components. Everything else below
// is lazy-loaded per-route so the initial JS bundle stays small.
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import AppLayout from './layouts/AppLayout';
import KioskLayout from './layouts/KioskLayout';
import ProtectedRoute from './components/ProtectedRoute';
import CustomerRoute from './components/CustomerRoute';
import PublicRoute from './components/PublicRoute';
import FunJumperPage from './pages/app/FunJumperPage';

// ─── Public / landing pages (lazy — not everyone visits every entry point) ──
const BookingPage              = lazyWithReload(() => import('./pages/BookingPage'));
const BookingSuccessPage       = lazyWithReload(() => import('./pages/BookingSuccessPage'));
const SignupPage               = lazyWithReload(() => import('./pages/SignupPage'));
const ForgotPasswordPage       = lazyWithReload(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage        = lazyWithReload(() => import('./pages/ResetPasswordPage'));
const SetPasswordPage          = lazyWithReload(() => import('./pages/SetPasswordPage'));
const SsoHandoffPage           = lazyWithReload(() => import('./pages/SsoHandoffPage'));
const CustomerLandingPage      = lazyWithReload(() => import('./pages/CustomerLandingPage'));
const PublicGiftCardPage       = lazyWithReload(() => import('./pages/PublicGiftCardPage'));
const SigningPage              = lazyWithReload(() => import('./pages/SigningPage'));
const MediaViewPage            = lazyWithReload(() => import('./pages/MediaViewPage'));
const CustomerGalleryPage      = lazyWithReload(() => import('./pages/CustomerGalleryPage'));
const MediaUnlockPage          = lazyWithReload(() => import('./pages/MediaUnlockPage'));
const GroupDetailsPage         = lazyWithReload(() => import('./pages/GroupDetailsPage'));
const GroupBookingDetailsPage  = lazyWithReload(() => import('./pages/public/GroupBookingDetailsPage'));
const GroupBookingSuccessPage  = lazyWithReload(() => import('./pages/GroupBookingSuccessPage'));
const LiveBoardPage            = lazyWithReload(() => import('./pages/public/LiveBoardPage'));

// ─── Kiosk flow (only loaded when /kiosk/* routes are hit) ──────────────────
const KioskLoginPage           = lazyWithReload(() => import('./pages/KioskLoginPage'));
const KioskScannerPage         = lazyWithReload(() => import('./pages/KioskScannerPage'));
// React.lazy exposes a single default export per call, so the two named
// kiosk payment result components are imported via two separate lazy calls.
// Both land in the same JS chunk because webpack de-dupes the shared module.
const KioskPaymentSuccess      = lazyWithReload(() => import('./pages/KioskPaymentResultPage').then((m) => ({ default: m.KioskPaymentSuccess })));
const KioskPaymentCancelled   = lazyWithReload(() => import('./pages/KioskPaymentResultPage').then((m) => ({ default: m.KioskPaymentCancelled })));

// ─── Mobile waiver (opened from kiosk QR) ───────────────────────────────────
const WaiverMobilePage         = lazyWithReload(() => import('./pages/WaiverMobilePage'));
const WaiverGuestPage          = lazyWithReload(() => import('./pages/WaiverGuestPage'));

// ─── Protected app pages (biggest share of the old bundle) ──────────────────
const Dashboard                = lazyWithReload(() => import('./pages/app/Dashboard'));
const ManifestPage             = lazyWithReload(() => import('./pages/app/ManifestPage'));
const InstructorSchedulePage   = lazyWithReload(() => import('./pages/app/InstructorSchedulePage'));
const CustomersPage            = lazyWithReload(() => import('./pages/app/CustomersPage'));
const CustomerDetailPage       = lazyWithReload(() => import('./pages/app/CustomerDetailPage'));
const CheckInPage              = lazyWithReload(() => import('./pages/app/CheckInPage'));
const SafetyPage               = lazyWithReload(() => import('./pages/app/SafetyPage'));
const SafetyDashboard          = lazyWithReload(() => import('./pages/app/safety/SafetyDashboard'));
const SafetyIncidents          = lazyWithReload(() => import('./pages/app/safety/SafetyIncidents'));
const SafetyIncidentDetail     = lazyWithReload(() => import('./pages/app/safety/SafetyIncidentDetail'));
const SafetyCompliance         = lazyWithReload(() => import('./pages/app/safety/SafetyCompliance'));
const SafetyAuditLog           = lazyWithReload(() => import('./pages/app/safety/SafetyAuditLog'));
const AircraftPage             = lazyWithReload(() => import('./pages/app/AircraftPage'));
const StaffPage                = lazyWithReload(() => import('./pages/app/StaffPage'));
const FinancePage              = lazyWithReload(() => import('./pages/app/FinancePage'));
const ReportsPage              = lazyWithReload(() => import('./pages/app/ReportsPage'));
const AdminPage                = lazyWithReload(() => import('./pages/app/AdminPage'));
const ProfilePage              = lazyWithReload(() => import('./pages/app/ProfilePage'));
const ProfileCompletionPage    = lazyWithReload(() => import('./pages/app/ProfileCompletionPage'));
const MyBookingsPage           = lazyWithReload(() => import('./pages/app/MyBookingsPage'));
const CustomerItemsPage        = lazyWithReload(() => import('./pages/app/CustomerItemsPage'));
const CustomerGiftCardsPage    = lazyWithReload(() => import('./pages/app/CustomerGiftCardsPage'));
const BookingsSchedulePage     = lazyWithReload(() => import('./pages/app/BookingsSchedulePage'));
const NewBookingPage           = lazyWithReload(() => import('./pages/app/NewBookingPage'));
const CheckInRoute             = lazyWithReload(() => import('./pages/app/CheckInRoute'));
const CustomerWalletPage       = lazyWithReload(() => import('./pages/app/CustomerWalletPage'));
const DocumentsPage            = lazyWithReload(() => import('./pages/app/DocumentsPage'));
const DocumentsConfigPage      = lazyWithReload(() => import('./pages/app/DocumentsConfigPage'));
const DocumentDetailPage       = lazyWithReload(() => import('./pages/app/DocumentDetailPage'));
const FieldPlacementPage       = lazyWithReload(() => import('./pages/app/FieldPlacementPage'));
const ManifestConfigPage       = lazyWithReload(() => import('./pages/app/ManifestConfigPage'));
const CheckInConfigPage        = lazyWithReload(() => import('./pages/app/CheckInConfigPage'));
const MediaPage                = lazyWithReload(() => import('./pages/app/MediaPage'));
const MediaConfigPage          = lazyWithReload(() => import('./pages/app/MediaConfigPage'));
const SmsConfigPage            = lazyWithReload(() => import('./pages/app/SmsConfigPage'));
const CustomerMediaPage        = lazyWithReload(() => import('./pages/app/CustomerMediaPage'));
const MyUploadsPage            = lazyWithReload(() => import('./pages/app/MyUploadsPage'));
const MyFinancialsPage         = lazyWithReload(() => import('./pages/app/MyFinancialsPage'));
const JumperProfilesPage       = lazyWithReload(() => import('./pages/app/JumperProfilesPage'));
const BlockTicketsPage         = lazyWithReload(() => import('./pages/app/BlockTicketsPage'));
const PilotOperationsPage      = lazyWithReload(() => import('./pages/app/PilotOperationsPage'));
const PackingPage              = lazyWithReload(() => import('./pages/app/PackingPage'));
const GiftCardPage             = lazyWithReload(() => import('./pages/app/GiftCardPage'));
const AnnouncementsPage        = lazyWithReload(() => import('./pages/app/AnnouncementsPage'));
const ParachuteManagementPage  = lazyWithReload(() => import('./pages/app/ParachuteManagementPage'));
const EquipmentPage            = lazyWithReload(() => import('./pages/app/EquipmentPage'));
const CustomerTypeConfigPage   = lazyWithReload(() => import('./pages/app/CustomerTypeConfigPage'));
const StaffRoleConfigPage      = lazyWithReload(() => import('./pages/app/StaffRoleConfigPage'));
const CustomerConfigPage       = lazyWithReload(() => import('./pages/app/CustomerConfigPage'));
const CustomerCredentialsConfigPage = lazyWithReload(() => import('./pages/app/CustomerCredentialsConfigPage'));
const CalendarBookingConfigPage = lazyWithReload(() => import('./pages/app/CalendarBookingConfigPage'));
const AnalyticsPage            = lazyWithReload(() => import('./pages/app/AnalyticsPage'));
const VoiceAgentPage           = lazyWithReload(() => import('./pages/app/VoiceAgentPage'));
const AnalyticsOverview        = lazyWithReload(() => import('./pages/app/analytics/AnalyticsOverview'));
const AnalyticsStaff           = lazyWithReload(() => import('./pages/app/analytics/AnalyticsStaff'));
const AnalyticsAircraft        = lazyWithReload(() => import('./pages/app/analytics/AnalyticsAircraft'));
const AnalyticsDocuments       = lazyWithReload(() => import('./pages/app/analytics/AnalyticsDocuments'));
const AnalyticsRevenue         = lazyWithReload(() => import('./pages/app/analytics/AnalyticsRevenue'));
const AnalyticsSafety          = lazyWithReload(() => import('./pages/app/analytics/AnalyticsSafety'));
const AnalyticsCustomers       = lazyWithReload(() => import('./pages/app/analytics/AnalyticsCustomers'));
const AnalyticsVideo           = lazyWithReload(() => import('./pages/app/analytics/AnalyticsVideo'));
// Small inline fallback while a chunk is loading. Intentionally lightweight —
// no heavy Loader2 icon import here to keep the initial bundle minimal.
const RouteFallback = () => (
  <div
    style={{
      minHeight: '60vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#64748b',
      fontSize: 14,
    }}
  >
    Loading…
  </div>
);
// Catches anything a route throws while rendering — including a page chunk
// that disappeared in a deploy — so it never blanks the whole app.
const RouteErrorBoundary = ({ children }) => {
  const { pathname } = useLocation();
  return <AppErrorBoundary resetKey={pathname}>{children}</AppErrorBoundary>;
};

// App Pages
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <SystemConfigProvider>
        <Router>
          {/* Suspense wraps every lazy-loaded route. Any page not in the
              initial eager bundle shows the fallback briefly while its chunk
              downloads (typically <200ms on a warm cache). */}
          <RouteErrorBoundary>
          <Suspense fallback={<RouteFallback />}>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/book" element={<BookingPage />} />
            <Route path="/book/event/:slug" element={<BookingPage />} />
            <Route path="/book/:slug" element={<BookingPage />} />
            <Route path="/booking-success" element={<BookingSuccessPage />} />
            <Route path="/group-booking-success" element={<GroupBookingSuccessPage />} />
            <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
            <Route path="/set-password" element={<SetPasswordPage />} />
            {/* Group SSO landing. Present on every build, harmless where SSO is
                not configured: the backend answers "not configured" and the
                page tells the visitor to sign in with a password. */}
            <Route path="/sso" element={<SsoHandoffPage />} />
            
            {/* Public Document Signing */}
            <Route path="/sign/:token" element={<SigningPage />} />
            
            {/* Public Group Booking Details */}
            <Route path="/booking/group/:token/details" element={<GroupDetailsPage />} />
            <Route path="/group-booking/:token" element={<GroupBookingDetailsPage />} />
            <Route path='/sell' element={<PublicRoute><CustomerLandingPage /></PublicRoute>} />
            {/* Public gift card purchase — the "Gift Card" CTA on the customer
                landing page. No login in this journey, so it is public; the
                price comes from the server's product row, never the client. */}
            <Route path="/gift-cards" element={<PublicGiftCardPage />} />
            {/* Public Media View (branded landing page) */}
            <Route path="/media/view/:token" element={<MediaViewPage />} />
            {/* Public Customer Gallery (branded delivery gallery — videos + photos) */}
            <Route path="/gallery/:token" element={<CustomerGalleryPage />} />
            {/* Paid-media unlock checkout (Path B paywall). The auto-edit locked
                gallery's CTA points here via CHECKOUT_URL_TEMPLATE —
                /unlock/{job_id}?item={item}. Public: no login in that journey. */}
            <Route path="/unlock/:jobId" element={<MediaUnlockPage />} />

            {/* Live Departure Board */}
            <Route path="/live-board" element={<LiveBoardPage />} />
            
            {/* Kiosk Routes (Public but restricted to kiosk role).
                KioskLayout applies the portrait frame + .kiosk class to
                every descendant — see src/layouts/KioskLayout.js. */}
            <Route path="/kiosk" element={<KioskLayout />}>
              {/* The bare /kiosk path had no index route, so KioskLayout rendered
                  its portrait frame around an empty Outlet — a blank page with
                  nothing on it but the EN/FR toggle. Same pattern as
                  /app → /app/dashboard. */}
              <Route index element={<Navigate to="/kiosk/login" replace />} />
              <Route path="login" element={<KioskLoginPage />} />
              <Route path="scanner" element={<KioskScannerPage />} />
              <Route path="payment-success" element={<KioskPaymentSuccess />} />
              <Route path="payment-cancelled" element={<KioskPaymentCancelled />} />
            </Route>
            
            {/* Mobile Waiver Signing (opened from kiosk QR code) */}
            <Route path="/waiver/:bookingId" element={<WaiverMobilePage />} />
            <Route path="/waiver/guest/:customerId" element={<WaiverGuestPage />} />
            
            {/* Protected App Routes */}
            <Route path="/app" element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }>
              <Route index element={<Navigate to="/app/dashboard" replace />} />
              <Route path="dashboard" element={
                <ProtectedRoute module="dashboard">
                  <Dashboard />
                </ProtectedRoute>
              } />
              <Route path="manifest" element={
                <ProtectedRoute module="manifest">
                  <ManifestPage />
                </ProtectedRoute>
              } />
              <Route path="manifest/config" element={
                <ProtectedRoute module="admin">
                  <ManifestConfigPage />
                </ProtectedRoute>
              } />
              <Route path="schedule" element={
                <ProtectedRoute module="schedule">
                  <InstructorSchedulePage />
                </ProtectedRoute>
              } />
              <Route path="customers" element={
                <ProtectedRoute module="customers">
                  <CustomersPage />
                </ProtectedRoute>
              } />
              <Route path="customers/config" element={
                <ProtectedRoute module="admin">
                  <CustomerTypeConfigPage />
                </ProtectedRoute>
              } />
              <Route path="customers/config/:slug/credentials" element={
                <ProtectedRoute module="admin">
                  <CustomerCredentialsConfigPage />
                </ProtectedRoute>
              } />
              <Route path="customer-config" element={
                <ProtectedRoute module="admin">
                  <CustomerConfigPage />
                </ProtectedRoute>
              } />
              <Route path="customers/:id" element={
                <ProtectedRoute module="customers">
                  <CustomerDetailPage />
                </ProtectedRoute>
              } />
              <Route path="check-in" element={
                <ProtectedRoute module="check-in">
                  <CheckInRoute />
                </ProtectedRoute>
              } />
              <Route path="check-in/config" element={
                <ProtectedRoute module="admin">
                  <CheckInConfigPage />
                </ProtectedRoute>
              } />
              {/* Waiver management is now integrated into the Documents page (Waiver Templates tab) */}
              <Route path="safety" element={
                <ProtectedRoute module="safety">
                  <SafetyPage />
                </ProtectedRoute>
              }>
                <Route index element={<SafetyDashboard />} />
                <Route path="dashboard" element={<SafetyDashboard />} />
                <Route path="incidents" element={<SafetyIncidents />} />
                <Route path="incidents/:id" element={<SafetyIncidentDetail />} />
                <Route path="compliance" element={<SafetyCompliance />} />
                <Route path="audit-logs" element={<SafetyAuditLog />} />
              </Route>
              <Route path="aircraft" element={
                <ProtectedRoute module="aircraft">
                  <AircraftPage />
                </ProtectedRoute>
              } />
              <Route path="staff" element={
                <ProtectedRoute module="staff">
                  <StaffPage />
                </ProtectedRoute>
              } />
              <Route path="staff/config" element={
                <ProtectedRoute module="admin">
                  <StaffRoleConfigPage />
                </ProtectedRoute>
              } />
              <Route path="finance" element={
                <ProtectedRoute module="finance">
                  <FinancePage />
                </ProtectedRoute>
              } />
              <Route path="gift-cards" element={
                <ProtectedRoute module="gift-cards">
                  <GiftCardPage />
                </ProtectedRoute>
              } />
              <Route path="reports" element={
                <ProtectedRoute module="reports">
                  <ReportsPage />
                </ProtectedRoute>
              } />
              <Route path="admin" element={
                <ProtectedRoute module="admin">
                  <AdminPage />
                </ProtectedRoute>
              } />
              <Route path="voice-agent" element={
                <ProtectedRoute module="voice-agent">
                  <VoiceAgentPage />
                </ProtectedRoute>
              } />
              {/* Back-compat: the old single "Call Logs" path now lives under the
                  AI Voice Agent dashboard. */}
              <Route path="calls" element={<Navigate to="/app/voice-agent" replace />} />
              <Route path="announcements" element={
                <ProtectedRoute module="manifest">
                  <AnnouncementsPage />
                </ProtectedRoute>
              } />
              <Route path="profile" element={
                <ProtectedRoute module="profile">
                  <ProfilePage />
                </ProtectedRoute>
              } />
              <Route path="profile/complete" element={
                <ProtectedRoute module="profile">
                  <ProfileCompletionPage />
                </ProtectedRoute>
              } />
              {/* My Bookings is customer-only. CustomerRoute redirects
                  non-customer accounts (no linked customerProfile) to the
                  dashboard instead of letting them fire customer-scoped
                  fetches that 4xx and blank the page (Bug 035). Note:
                  `bookings/new` below is deliberately NOT wrapped — staff
                  create bookings there too. */}
              <Route path="bookings" element={
                <ProtectedRoute module="bookings">
                  <CustomerRoute>
                    <MyBookingsPage />
                  </CustomerRoute>
                </ProtectedRoute>
              } />
              {/* Staff weekly/day booking planner. Deliberately NOT wrapped in
                  CustomerRoute — this is the staff-facing bookings calendar,
                  distinct from the customer-only "My Bookings" list above. */}
              <Route path="bookings/schedule" element={
                <ProtectedRoute module="bookings">
                  <BookingsSchedulePage />
                </ProtectedRoute>
              } />
              <Route path="bookings/new" element={
                <ProtectedRoute module="bookings">
                  <NewBookingPage />
                </ProtectedRoute>
              } />
              {/* Dropzone shop — the customer-facing Items (merchandise)
                  catalog. Customer-only like My Bookings: staff sell/see items
                  from the customer profile page, not from their own portal. */}
              <Route path="shop" element={
                <ProtectedRoute module="items">
                  <CustomerRoute>
                    <CustomerItemsPage />
                  </CustomerRoute>
                </ProtectedRoute>
              } />
              <Route path="wallet" element={
                <ProtectedRoute module="wallet">
                  <CustomerWalletPage />
                </ProtectedRoute>
              } />
              {/* Customer gift cards — buy a card (wallet or Clover) and
                  email it to someone. Customer-only like the shop: staff
                  issue/manage cards from /app/gift-cards. */}
              <Route path="my-gift-cards" element={
                <ProtectedRoute module="my-gift-cards">
                  <CustomerRoute>
                    <CustomerGiftCardsPage />
                  </CustomerRoute>
                </ProtectedRoute>
              } />
              <Route path="documents" element={
                <ProtectedRoute module="documents">
                  <DocumentsPage />
                </ProtectedRoute>
              } />
              {/* Documents → Config page (own dedicated route, matches the
                  /app/<module>/config pattern used by Media/Manifest/etc.) */}
              <Route path="documents/config" element={
                <ProtectedRoute module="admin">
                  <DocumentsConfigPage />
                </ProtectedRoute>
              } />
              {/* Legacy redirect: the old Doc Automation page now lives at
                  /documents/config. Placed before /documents/:id so the
                  literal "automation" segment wins over the dynamic :id. */}
              <Route path="documents/automation" element={<Navigate to="/app/documents/config" replace />} />
              {/* Template-edit route — placed BEFORE the dynamic
                  /documents/:id/fields so the literal "templates" segment
                  wins route specificity in both React Router v6 and the
                  read order. */}
              <Route path="documents/templates/:id/fields" element={
                <ProtectedRoute module="admin">
                  <FieldPlacementPage />
                </ProtectedRoute>
              } />
              <Route path="documents/:id" element={
                <ProtectedRoute module="documents">
                  <DocumentDetailPage />
                </ProtectedRoute>
              } />
              <Route path="documents/:id/fields" element={
                <ProtectedRoute module="documents">
                  <FieldPlacementPage />
                </ProtectedRoute>
              } />
              <Route path="media" element={
                <ProtectedRoute module="media">
                  <MediaPage />
                </ProtectedRoute>
              } />
              <Route path="media/config" element={
                <ProtectedRoute module="admin">
                  <MediaConfigPage />
                </ProtectedRoute>
              } />
              <Route path="jumper-profiles" element={
                <ProtectedRoute module="admin">
                  <JumperProfilesPage />
                </ProtectedRoute>
              } />
              <Route path="block-tickets" element={
                <ProtectedRoute module="admin">
                  <BlockTicketsPage />
                </ProtectedRoute>
              } />
              <Route path="parachutes" element={
                <ProtectedRoute module="admin">
                  <ParachuteManagementPage />
                </ProtectedRoute>
              } />
              <Route path="equipment" element={
                <ProtectedRoute module="admin">
                  <EquipmentPage />
                </ProtectedRoute>
              } />
                
              <Route path="media/analytics" element={
                <ProtectedRoute module="media">
                  <AnalyticsPage />
                </ProtectedRoute>
              }>
                <Route index element={<AnalyticsVideo />} />
              </Route>
              <Route path="analytics" element={
                <ProtectedRoute module="dashboard">
                  <AnalyticsPage />
                </ProtectedRoute>
              }>
                <Route index element={<AnalyticsOverview />} />
                <Route path="overview" element={<AnalyticsOverview />} />
                <Route path="staff" element={<AnalyticsStaff />} />
                <Route path="aircraft" element={<AnalyticsAircraft />} />
                <Route path="documents" element={<AnalyticsDocuments />} />
                <Route path="revenue" element={<AnalyticsRevenue />} />
                <Route path="safety" element={<AnalyticsSafety />} />
                <Route path="customers" element={<AnalyticsCustomers />} />
                <Route path="video" element={<AnalyticsVideo />} />
              </Route>
              <Route path="my-uploads" element={
                <ProtectedRoute module="media">
                  <MyUploadsPage />
                </ProtectedRoute>
              } />
              <Route path="my-financials" element={<MyFinancialsPage />} />
              <Route path="my-media" element={
                <ProtectedRoute module="my-media">
                  <CustomerMediaPage />
                </ProtectedRoute>
              } />
              <Route path="fun-jumper" element={
                <ProtectedRoute module="fun-jumper">
                  <FunJumperPage />
                </ProtectedRoute>
              } />
              <Route path="pilot-ops" element={
                <ProtectedRoute module="admin">
                  <PilotOperationsPage />
                </ProtectedRoute>
              } />
              <Route path="packing" element={
                <ProtectedRoute module="packing">
                  <PackingPage />
                </ProtectedRoute>
              } />
              <Route path="calendar-config" element={
                <ProtectedRoute module="admin">
                  <CalendarBookingConfigPage />
                </ProtectedRoute>
              } />
              <Route path="sms-config" element={
                <ProtectedRoute module="admin">
                  <SmsConfigPage />
                </ProtectedRoute>
              } />
            </Route>
            
            {/* Catch all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </Suspense>
          </RouteErrorBoundary>
          <Toaster position="top-right" richColors />
        </Router>
        </SystemConfigProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
